import type { Session } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useAuthStore } from '@/stores/auth-store';

const REFRESH_MARGIN_MS = 60_000;
const MIN_REFRESH_INTERVAL_MS = 5 * 60_000;

let refreshTimer: ReturnType<typeof setTimeout> | undefined;

const clearRefreshTimer = (): void => {
  if (refreshTimer !== undefined) {
    clearTimeout(refreshTimer);
    refreshTimer = undefined;
  }
};

const isInvalidRefreshTokenError = (message: string | undefined): boolean => {
  if (!message) {
    return false;
  }
  const normalized = message.toLowerCase();
  return (
    normalized.includes('invalid refresh token') || normalized.includes('refresh token not found')
  );
};

const isBrowserEnvironment = (): boolean =>
  typeof globalThis === 'object' && typeof (globalThis as { window?: unknown }).window === 'object';

const scheduleNextRefresh = (session: Session | undefined): void => {
  clearRefreshTimer();
  if (!isBrowserEnvironment() || !session?.refresh_token) {
    return;
  }

  const expiresAtSec = Number(session.expires_at ?? Number.NaN);
  const expiresAtMs = Number.isFinite(expiresAtSec) ? expiresAtSec * 1000 : undefined;
  const delayFromExpiry =
    expiresAtMs === undefined
      ? MIN_REFRESH_INTERVAL_MS
      : Math.max(REFRESH_MARGIN_MS, expiresAtMs - Date.now() - REFRESH_MARGIN_MS);
  const delay = Math.max(MIN_REFRESH_INTERVAL_MS, delayFromExpiry);

  refreshTimer = setTimeout(() => {
    void attemptRefresh();
  }, delay);
};

const handleInvalidRefreshToken = async (): Promise<void> => {
  clearRefreshTimer();
  const signOut = useAuthStore.getState().signOut;
  try {
    await signOut();
  } catch {
    // Swallow errors to avoid surfacing in console; store handles its own fallbacks.
  }
};

const attemptRefresh = async (): Promise<void> => {
  try {
    const { data, error } = await supabase.auth.refreshSession();
    if (error) {
      if (isInvalidRefreshTokenError(error.message)) {
        await handleInvalidRefreshToken();
        return;
      }
      const {
        data: { session },
      } = await supabase.auth.getSession();
      scheduleNextRefresh(session ?? undefined);
      return;
    }
    scheduleNextRefresh(data.session ?? undefined);
  } catch (error) {
    const message = error instanceof Error ? error.message : String(error);
    if (isInvalidRefreshTokenError(message)) {
      await handleInvalidRefreshToken();
      return;
    }
    const {
      data: { session },
    } = await supabase.auth.getSession();
    scheduleNextRefresh(session ?? undefined);
  }
};

export const handleSupabaseSessionChange = (session: Session | undefined): void => {
  if (!isBrowserEnvironment()) {
    return;
  }
  scheduleNextRefresh(session);
};
